<template>
  <div>
    <div class="row mt-3">
      <div class="col-md-3">
        <div v-if="loading" class="text-muted p-2">Carregando…</div>
        <ul v-else class="list-group">
          <li
            v-for="t in templates"
            :key="t.key"
            class="list-group-item"
            :class="{ 'list-group-item-active': selectedKey === t.key }"
            style="cursor: pointer"
            @click="selectEmail(t)"
          >
            <div class="d-flex justify-content-between align-items-start">
              <div>
                <strong>{{ t.name }}</strong>
                <div><small class="text-muted">{{ t.key }}</small></div>
              </div>
              <div class="text-right">
                <span v-if="t.critical" class="badge badge-warning">crítico</span>
                <span v-if="isCustomized(t)" class="badge badge-success">personalizado</span>
                <span v-if="t.variants && t.variants.length > 1" class="badge badge-info">{{ t.variants.length }} variantes</span>
              </div>
            </div>
          </li>
        </ul>
      </div>

      <div class="col-md-9">
        <div v-if="!selected" class="text-muted text-center mt-5">
          Selecione um e-mail à esquerda para editar.
        </div>

        <div v-else class="ibox">
          <div class="ibox-title">
            <h3 class="mb-0">{{ selected.name }}</h3>
            <small class="text-muted">{{ selected.key }}</small>
          </div>

          <div class="ibox-content">
            <div v-if="selected.variants && selected.variants.length > 1" class="btn-group mb-3">
              <button
                v-for="v in selected.variants"
                :key="v.variant"
                class="btn btn-sm"
                :class="v.variant === selectedVariant ? 'btn-primary' : 'btn-outline'"
                @click="selectVariant(v.variant)"
              >
                {{ v.label }}
              </button>
            </div>

            <p v-if="selected.variables && selected.variables.length" class="mb-3">
              <small class="text-muted">Variáveis disponíveis:</small>
              <code v-for="v in selected.variables" :key="v" class="mr-1">{{ variableTag(v) }}</code>
            </p>

            <div class="form-group">
              <label>Assunto</label>
              <input v-model="subject" class="form-control" type="text">
            </div>

            <div class="row">
              <div class="col-md-6 form-group">
                <label>Corpo (HTML)</label>
                <textarea
                  v-model="bodyHtml"
                  class="form-control"
                  rows="22"
                  spellcheck="false"
                  style="font-family: monospace; font-size: 12px; resize: none"
                ></textarea>
              </div>
              <div class="col-md-6 form-group">
                <label>Pré-visualização (dados de exemplo)</label>
                <iframe
                  :srcdoc="previewHtml"
                  sandbox=""
                  title="Pré-visualização do e-mail"
                  style="width: 100%; height: 520px; border: 1px solid #e3e8ef; border-radius: 4px; background: #fff"
                ></iframe>
              </div>
            </div>

            <div class="form-group d-flex align-items-center flex-wrap mt-2">
              <input
                v-model="testEmail"
                type="email"
                class="form-control mr-2 mb-1"
                style="max-width: 260px"
                placeholder="e-mail p/ teste (padrão: você)"
              >
              <button
                v-if="currentVariant && currentVariant.customized"
                class="btn btn-outline mr-2 mb-1"
                :disabled="restoring"
                @click="restore()"
              >
                {{ restoring ? 'Restaurando…' : 'Restaurar padrão' }}
              </button>
              <button class="btn btn-outline mr-2 mb-1" :disabled="sending" @click="sendTest()">
                {{ sending ? 'Enviando…' : 'Enviar teste' }}
              </button>
              <button class="btn btn-primary mb-1" :disabled="saving" @click="save()">
                {{ saving ? 'Salvando…' : 'Salvar' }}
              </button>
            </div>

            <div v-if="sentPreview" class="mt-3">
              <label>Teste enviado para {{ sentPreview.sent_to }} — assim que ficou:</label>
              <div class="border rounded p-2 mb-2">
                <strong>Assunto:</strong> {{ sentPreview.subject }}
              </div>
              <iframe
                :srcdoc="sentPreview.body_html"
                sandbox=""
                title="E-mail de teste enviado"
                style="width: 100%; height: 420px; border: 1px solid #e3e8ef; border-radius: 4px; background: #fff"
              ></iframe>
            </div>
          </div>
        </div>
      </div>
    </div>

    <Modal v-model="showMissing">
      <div slot="header">Variáveis faltando</div>
      <p>Não é possível salvar sem as variáveis obrigatórias deste e-mail. Corrija antes de continuar:</p>
      <ul>
        <li v-for="(e, i) in missingVars" :key="i">{{ e }}</li>
      </ul>
      <div class="form-group mt-3">
        <button class="btn btn-primary btn-lg" @click="missingVars = []">Entendi</button>
      </div>
    </Modal>
  </div>
</template>

<script>
import axios from '@/plugins/axios'
import Modal from '@/components/shares/specialTag/Modal'

const SAMPLE = {
  cliente_nome: 'João da Silva',
  cliente_email: 'joao.silva@exemplo.com',
  conta_nome: 'Empresa Exemplo Ltda',
  company_label: 'Empresa Exemplo Ltda',
  parceiro_nome: 'Parceiro Exemplo',
  link: 'https://app.brobot.com.br/exemplo',
  otp_code: '123456',
  token: 'exemplo-token',
  dias: '3',
  app_nome: 'Caça Multas',
  placa: 'ABC1D23',
  ait: 'AB1234567',
  nome_condutor: 'Maria Souza',
  titulo: 'Aviso importante',
  descricao: 'Este é um texto de exemplo.'
}

const DEADLINE_PREFIX = 'driver_indication/deadline_alert_'
const DEADLINE_TITLES = {
  10: 'Lembrete de prazo',
  5: 'Alerta de prazo',
  3: 'Atenção - Prazo crítico',
  2: 'URGENTE - Faltam 2 dias',
  1: 'URGENTE - Último dia'
}

function sampleFor(key) {
  if (!key || !key.startsWith(DEADLINE_PREFIX)) return SAMPLE
  const dias = Number(key.slice(DEADLINE_PREFIX.length))
  return {
    ...SAMPLE,
    cliente_nome: 'Patrícia Lemos',
    placa: 'LUH7J14',
    ait: 'NW01268831',
    nome_condutor: 'Cleyton Carvalho de Araújo',
    status: 'Solicitado confirmação do motorista',
    prazo: '14/08/2026',
    dias: String(dias),
    titulo: DEADLINE_TITLES[dias] || 'Lembrete de prazo',
    link: 'https://platform.brobot.com.br/caca-condutores/indicacoes'
  }
}

export default {
  name: 'EmailTemplates',
  components: { Modal },
  data() {
    return {
      templates: [],
      selectedKey: null,
      selectedVariant: 'default',
      subject: '',
      bodyHtml: '',
      debouncedBody: '',
      previewTimer: null,
      testEmail: '',
      sentPreview: null,
      loading: false,
      saving: false,
      sending: false,
      restoring: false,
      missingVars: []
    }
  },
  computed: {
    showMissing: {
      get() { return this.missingVars.length > 0 },
      set(v) { if (!v) this.missingVars = [] }
    },
    selected() {
      return this.templates.find(t => t.key === this.selectedKey) || null
    },
    currentVariant() {
      if (!this.selected) return null
      return this.selected.variants.find(v => v.variant === this.selectedVariant) || this.selected.variants[0]
    },
    previewHtml() {
      return this.renderSample(this.debouncedBody)
    }
  },
  watch: {
    bodyHtml(val) {
      clearTimeout(this.previewTimer)
      this.previewTimer = setTimeout(() => { this.debouncedBody = val }, 350)
    }
  },
  created() {
    this.fetch()
  },
  methods: {
    variableTag(name) {
      return `{{${name}}}`
    },
    isCustomized(t) {
      return (t.variants || []).some(v => v.customized)
    },
    renderSample(html) {
      const sample = sampleFor(this.selectedKey)
      return (html || '').replace(/\{\{\s*([\w.]+)\s*\}\}/g, (match, key) => sample[key] || `{{${key}}}`)
    },
    fetch() {
      this.loading = true
      axios.get('/admin/email_templates')
        .then(res => { this.templates = (res && res.data) || [] })
        .catch(err => this.$notify.addError(err))
        .finally(() => { this.loading = false })
    },
    selectEmail(t) {
      this.selectedKey = t.key
      this.selectedVariant = (t.variants[0] && t.variants[0].variant) || 'default'
      this.loadVariant()
    },
    selectVariant(variant) {
      this.selectedVariant = variant
      this.loadVariant()
    },
    loadVariant() {
      this.sentPreview = null
      const v = this.currentVariant
      this.subject = v ? v.subject : ''
      this.bodyHtml = v ? v.body_html : ''
      clearTimeout(this.previewTimer)
      this.debouncedBody = this.bodyHtml
    },
    localMissing() {
      const content = (this.subject || '') + ' ' + (this.bodyHtml || '')
      const required = (this.selected && this.selected.required) || []
      return required.filter(v => !new RegExp('\\{\\{\\s*' + v + '\\s*\\}\\}').test(content))
    },
    save() {
      const miss = this.localMissing()
      if (miss.length) {
        this.missingVars = miss.map(v => 'Faltando a variável obrigatória ' + this.variableTag(v))
        return
      }
      this.saving = true
      axios.put('/admin/email_templates/save', {
        key: this.selectedKey,
        variant: this.selectedVariant,
        subject: this.subject,
        body_html: this.bodyHtml
      })
        .then(() => { this.$notify.add('Template salvo!'); this.fetch() })
        .catch(err => {
          if (err && Array.isArray(err.errors) && err.errors.length) this.missingVars = err.errors
          else this.$notify.addError(err)
        })
        .finally(() => { this.saving = false })
    },
    restore() {
      this.restoring = true
      axios.delete('/admin/email_templates/restore', {
        params: { key: this.selectedKey, variant: this.selectedVariant }
      })
        .then(() => { this.$notify.add('Template restaurado para o padrão!'); this.fetch() })
        .catch(err => this.$notify.addError(err))
        .finally(() => { this.restoring = false })
    },
    sendTest() {
      this.sending = true
      axios.post('/admin/email_templates/send_test', {
        key: this.selectedKey,
        variant: this.selectedVariant,
        subject: this.subject,
        body_html: this.bodyHtml,
        email: this.testEmail || undefined
      })
        .then(res => {
          this.sentPreview = res.data
          this.$notify.add(`E-mail de teste enviado para ${res.data.sent_to}`)
        })
        .catch(err => this.$notify.addError(err))
        .finally(() => { this.sending = false })
    }
  }
}
</script>

<style scoped>
.list-group-item {
  background-color: var(--card-bg);
  color: var(--primary-text-color);
  border-color: var(--border-color);
  border-left: 3px solid transparent;
}
.list-group-item small {
  color: var(--secondary-text-color);
}
.list-group-item-active {
  background-color: var(--btn-hover-bg);
  border-left-color: var(--primary-color);
}
.list-group-item-active strong {
  color: var(--primary-color);
}
</style>
